<template>
    <!-- 영향인자를 제외한 성분분석만 포함하도록 수정할 계획입니다. author 선구 -->
    <div v-if="resultData" class="stackChartContainer">
        <div ref="stackChart" style="width: 75%; height: 100%"></div>
        <div ref="pieChart" style="width: 25%; height: 100%"></div>
    </div>
</template>

<script>
import * as echarts from "echarts";

export default {
    props: ["resultData", "detailInfo", "hideInfoPanel"],
    data() {
        return {
            stackChart: null,
            pieChart: null,
            selectedYearIndex: 0,
            showTotalSum: true, // 전체 합계 표시 여부
        };
    },
    computed: {
        chartType() {
            return !this.isEmpty(this.detailInfo.dispType) && this.detailInfo.dispType !== "Area"
                ? this.detailInfo.dispType.toLowerCase()
                : "line";
        },
        labels() {
            return this.resultData?.values?.regDt || [];
        },
        ptMap() {
            const map = new Map(
                this.resultData.headers
                    .filter((header) => header.roleType == "Component")
                    .map((header) => [header.key, header.ptName]),
            );
            return map;
        },
        seriesData() {
            return Array.from(this.ptMap.keys()).reduce((acc, ptIdx) => {
                if (ptIdx === "regDt") return acc;
                const ptName = this.ptMap.get(ptIdx);

                acc.push({
                    name: ptIdx, // resultData.headers의 key 값 사용
                    data: this.resultData.values[ptIdx],
                    unit: this.unitCalc(ptName),
                });
                return acc;
            }, []);
        },
        pieData() {
            return this.seriesData.map((series) => {
                const value = this.showTotalSum
                    ? series.data.reduce((sum, val) => sum + (val || 0), 0)
                    : series.data[this.selectedYearIndex];

                return {
                    name: series.name,
                    value,
                    unit: series.unit,
                };
            });
        },
    },
    mounted() {
        console.log("this.resultData.headers", this.resultData.headers);
        if (this.resultData) {
            this.initCharts();
            window.addEventListener("resize", this.resizeCharts);
        }
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.resizeCharts);
        this.stackChart?.dispose();
        this.pieChart?.dispose();
    },
    watch: {
        resultData(newValue) {
            if (newValue) {
                this.initCharts();
            }
        },
        hideInfoPanel() {
            this.resizeCharts();
        },
    },
    methods: {
        /**
         * 값을 축약하여 표시 (K, M, G 단위)
         */
        formatValue(value) {
            if (!value) return "-";

            const units = [
                { threshold: 1e9, suffix: "G" },
                { threshold: 1e6, suffix: "M" },
                { threshold: 1e3, suffix: "K" },
            ];

            for (const { threshold, suffix } of units) {
                if (value >= threshold) {
                    const result = value / threshold;
                    return result.toFixed(1) + suffix;
                }
            }

            return value.toFixed(1);
        },

        unitCalc(ptName) {
            const header = this.resultData.headers.find((item) => item.ptName === ptName);
            return header?.dispUnit || header?.unitSmallCode || "-";
        },
        initCharts() {
            if (this.stackChart) this.stackChart.dispose();
            if (this.pieChart) this.pieChart.dispose();

            this.stackChart = echarts.init(this.$refs.stackChart);
            const stackOption = {
                grid: {
                    width: "85%",
                },
                legend: {
                    bottom: 5,
                },
                tooltip: {
                    trigger: "axis",
                    axisPointer: {
                        type: "shadow",
                    },
                    formatter: (params) => {
                        const items = params
                            .map(
                                (param) => `
                            <div style="display: flex; justify-content: space-between;">
                                <div>
                                    ${param.marker}
                                    <span>${param.seriesName} (${param.data.unit})</span>
                                </div>
                                <span style="font-weight: bold;">
                                    ${this.formatValue(param.data.value)}
                                </span>
                            </div>`,
                            )
                            .join("");

                        return `
                            <div style="font-size: 12px;">
                                <div class="mb-3">
                                    <p class="m-0 p-0">${params[0].axisValue}</p>
                                    ${items}
                                </div>
                            </div>`;
                    },
                },
                xAxis: {
                    type: "category",
                    data: this.labels,
                },
                yAxis: {
                    type: "value",
                    axisLabel: {
                        align: "right",
                        formatter: (value) => this.formatValue(value),
                    },
                },
                series: this.seriesData.map((series) => ({
                    name: series.name,
                    type: this.chartType,
                    stack: "Total",
                    smooth: true,
                    areaStyle: {},
                    data: series.data.map((value) => ({ value, unit: series.unit })),
                    emphasis: { focus: "series" },
                })),
            };

            this.stackChart.setOption(stackOption);

            this.pieChart = echarts.init(this.$refs.pieChart);
            this.updatePieChart();

            this.stackChart.on("updateAxisPointer", (event) => {
                if (!event.axesInfo[0]) return;
                const index = event.axesInfo[0].value;

                // 호버 시 전체 합계 모드 해제하고 특정 인덱스 데이터 표시
                this.showTotalSum = false;

                if (index !== this.selectedYearIndex) {
                    this.selectedYearIndex = index;
                    this.updatePieChart();
                }
            });

            // 차트 밖으로 마우스가 나갈 때 - 전체 합계 표시
            this.stackChart.on("globalout", () => {
                this.showTotalSum = true;
                this.updatePieChart();
            });
        },
        updatePieChart() {
            const pieOption = {
                tooltip: {
                    trigger: "item",
                    formatter: (params) => {
                        const unit = params.data?.unit || "-";
                        return `
                            <div style="font-size: 12px;">
                                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                                    ${params.marker} 
                                    <span>${params.name} (${unit})</span>&nbsp;:&nbsp;
                                    <span style="font-weight: bold;">
                                        ${this.formatValue(params.value)}
                                    </span>
                                    (${params.percent}%)
                                </div>
                            </div>`;
                    },
                },
                series: [
                    {
                        name: "제품",
                        type: "pie",
                        radius: ["20%", "80%"],
                        center: ["50%", "50%"],
                        data: this.pieData,
                        label: {
                            show: true,
                            position: "inside",
                            formatter: "{b}\n({d}%)",
                            fontSize: 12,
                            color: "#FFF",
                        },
                        labelLine: {
                            show: false,
                        },
                        emphasis: {
                            itemStyle: {
                                shadowBlur: 10,
                                shadowOffsetX: 0,
                                shadowOffsetY: 0,
                                shadowColor: "rgba(0, 0, 0, 0.5)",
                            },
                        },
                    },
                ],
            };
            this.pieChart.setOption(pieOption);
        },
        resizeCharts() {
            this.stackChart?.resize();
            this.pieChart?.resize();
        },
    },
};
</script>

<style scoped>
.stackChartContainer {
    width: 100%;
    height: 100%;
    display: flex;
}
</style>
